/**
 * CollapsibleList — bounded, collapsible wrapper for long requirement/entry lists.
 *
 * Solves the "non-stop scrolling" problem on pursuit pages where extracted
 * requirements can run to hundreds of rows. Provides:
 *   - A header strip with item count + Collapse/Expand toggle
 *   - When expanded, the list scrolls WITHIN a bounded max-height box instead
 *     of pushing the whole page down
 *   - Optional "start collapsed" for very long lists
 */
import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';

export default function CollapsibleList({
  count,
  label = 'items',
  children,
  defaultCollapsed = false,
  maxHeightClass = 'max-h-[560px]',
  testId,
  // When the list is short, skip the bounded scroll box entirely
  boundThreshold = 8,
}) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const shouldBound = (count || 0) > boundThreshold;

  return (
    <div data-testid={testId}>
      {/* Header strip */}
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2 bg-slate-100 hover:bg-slate-200/70 border-y border-slate-200 transition-colors text-left"
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        <span className="flex items-center gap-2 text-xs font-semibold text-slate-600 uppercase tracking-wider">
          {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {count ?? 0} {label}
        </span>
        <span className="text-[11px] text-slate-400 font-medium">
          {collapsed ? 'Expand' : 'Collapse'}
        </span>
      </button>

      {/* Body — bounded scroll when long */}
      {!collapsed && (
        <div
          className={cn(
            shouldBound && `${maxHeightClass} overflow-y-auto`,
          )}
          data-testid={testId ? `${testId}-body` : undefined}
        >
          {children}
        </div>
      )}
    </div>
  );
}
